import React, { useState } from 'react'
import { SubformProps, Validation } from "."
import { Applicant, Validate } from '@oneethos/shared'
import ApplicantForm from './applicant-form'
import EmploymentForm from './employment-form'
import { Spinner } from '@fluentui/react'

export const CoborrowerFields = ({ formData, update }: Partial<SubformProps>) => {
  const handleCoborrowerChange = (data: string | Record<string, unknown>, val?: unknown) => {
    if (typeof data === 'string') {
      const k = 'co' + data[0].toUpperCase() + data.substring(1)
      update(k, val)
    } else {
      const obj = {}
      Object.entries(data).forEach(([k, v]) => {
        obj['co' + k[0].toUpperCase() + k.substring(1)] = v
      })
      update(obj)
    }
  }

  const coborrower = Applicant.fromLoanAppCoBorrower(formData, true)
  const coRequired = (
    formData.prequalResultCombined === 'pass' &&
    formData.prequalResult !== 'pass'
  )

  return <>
    {/*
      we only give them the option to add a coborrower at this step if the primary 
      borrower passed prequal on their own;
      otherwise it should be required if they've made it to this step
    */}
    <h2>Co-Borrower Information</h2>
    {!coRequired ? <>
      <div className="form-group">
        <label>
          Would you like to include a Co-Borrower in your application?
          Including a co-borrower will result in a hard credit inquiry for the co-borrower,
          which can impact their credit score.
        </label>
        <select
          className="form-select"
          value={formData.includeCoBorrower}
          onChange={ev => update('includeCoBorrower', ev.target.value)}
        >
          <option value='no'>No</option>
          <option value='yes'>Yes</option>
        </select>
      </div>
    </> : null}
    {coRequired || formData.includeCoBorrower === 'yes' ? <>
      <ApplicantForm
        applicant={coborrower}
        update={handleCoborrowerChange}
        address={formData.coBorrowerAddress || { country: 'United States' }}
        onAddressChange={addr => update('coBorrowerAddress', addr)}
        disableCoreChanges={!!formData.prequalResultCombined}
      />
      <h2>Co-Borrower Employment Information</h2>
      <EmploymentForm
        employment={coborrower}
        onChange={handleCoborrowerChange}
        disableIncomeChanges={!!formData.prequalResultCombined}
      />
    </> : null}
  </>
}

export const Coborrower = ({ schema, formData, saving, update, save, onError }: SubformProps) => {
  const [validation, setValidation] = useState<Record<string, string>>({})

  const validate = () => {
    const fields = [
      'coFirstName',
      'coLastName',
      'coSsn',
      'coPhone',
      'coEmail',
      'coBirthDate',
      'coDriversLicenseNo',
      'coDriversLicenseIssueDate',
      'coDriversLicenseIssuingState',
      'coDriversLicenseExpirationDate',
      'coCitizenship',
      'coBorrowerAddress',
      'coOccupation',
      'coEmployerName',
      'coStatedGrossAnnualIncome',
      'coYearsAtEmployer'
    ]

    if (formData.includeCoBorrower !== 'yes') {
      return {}
    }

    const results = Validate.validateFields(schema, formData, fields)

    if (!['yes', 'no'].includes(formData.coHasMortgage)) {
      results['hasMortgage'] = 'Please indicate whether or not coborrower has a mortgage'
    }

    if (Number(formData.coYearsAtEmployer) < 2 && !formData.coPreviousEmployment) {
      results['previousEmployment'] = 'Please add previous employment'
    }

    return results
  }

  return <>
    <CoborrowerFields formData={formData} update={update} />

    <div>
      <button
        type="button"
        className="btn btn-primary"
        disabled={saving}
        onClick={() => {
          const v = validate()
          setValidation(v)
          if (Object.keys(v).length === 0) {
            save()
          }
        }}
      >{saving ? <Spinner /> : 'Continue'}</button>
    </div>
    {validation ? (
      <Validation validation={validation} /> || <div className="alert alert-info">Form is valid</div>
    ) : null}
  </>
}

export default Coborrower